import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { NamedLink, ResponsiveImage } from '../../../components';
import { FormattedMessage } from '../../../util/reactIntl';
import businessCategoryConfig from '../../../business-category-config';
import CatalogItem from '../CatalogItem/CatalogItem';

import css from './ProductItem.css';

const ProductItem = ({
  catalogProduct = null,
  intl,
  isExpandedItems,
  isHighlighted = false,
  highlightedItemId = null,
  onUserInteraction,
  isNewlyCreated = false,
}) => {
  const { attributes: {
    businessCategory,
    description,
    minBookingDays,
    modelCode,
    parts,
    price: { amount, currency },
    title },
    catalogItems = []
  } = catalogProduct;

  const containsHighlightedItem = catalogItems.some(item => item.id?.uuid === highlightedItemId);
  const [isExpanded, setIsExpanded] = useState(isExpandedItems || isHighlighted || containsHighlightedItem);
  const itemRef = useRef(null);
  const location = useLocation();

  const catalogProductId = catalogProduct.id.uuid;
    const itemSearch = location.search 
    ? `${location.search}&productId=${catalogProductId}` 
    : `?productId=${catalogProductId}`;
  const businessCategoryLabel = businessCategoryConfig.find(item => item.key === businessCategory)?.translation;

  const image =
    catalogProduct.images && catalogProduct.images.length > 0 ? catalogProduct.images[0] : null;

  const handleExpandClick = event => {
    event.preventDefault();
    if (onUserInteraction) onUserInteraction(); 
    setIsExpanded(prevState => !prevState);
  };

  useEffect(() => {
    if (isHighlighted || containsHighlightedItem) {
      setIsExpanded(true);
    } else {
      setIsExpanded(isExpandedItems);
    }
  }, [isExpandedItems, isHighlighted, containsHighlightedItem]);

  useEffect(() => {
    if (isHighlighted && !highlightedItemId && itemRef.current) {
      const timer = setTimeout(() => {
        itemRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start', 
        });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isHighlighted, highlightedItemId]);

  return (
    <li 
      ref={itemRef}
      className={css.listItem} 
      key={catalogProductId}
    >
      <div className={classNames(css.itemContainer, { [css.expanded]: isExpanded, [css.highlighted]: isHighlighted })}>
        <div className={css.imgContainer}>
          <div className={css.aspectWrapper}>
            <ResponsiveImage
              rootClassName={css.rootForImage}
              alt={title}
              image={image}
              variants={['scaled-medium']}
            />
          </div>
        </div>
        <div className={css.detailsContainer}>
          <div className={css.details}>
            {isNewlyCreated && (
              <span className={css.newProductBadge}>
                {intl.formatMessage({ id: 'Console.newlyAddedProduct' }, { defaultMessage: 'Nowo dodany produkt' })}
              </span>
            )}
            <p className={css.title}>{title}</p>
            <p className={css.price}>
              <span className={css.priceValue}>{amount / 100} {currency}</span>
              <span className={css.perUnit}>{intl.formatMessage({ id: 'Console.perDay' })}</span>
            </p>
            <div className={css.wrapper}>
              <p className={css.text}>
                {intl.formatMessage({ id: 'Console.businessCategory' })}: {intl.formatMessage({ id: businessCategoryLabel })}
              </p>
              <p className={css.text}>
                {intl.formatMessage({ id: 'Console.modelCode' })}: {modelCode}
              </p>
            </div>
            <div className={css.wrapper}>
              <p className={css.text}>
                {intl.formatMessage({ id: 'Console.minBookingDays' })}: {minBookingDays}
              </p>
              <p className={css.text}>
                {intl.formatMessage({ id: 'Console.itemsNumber' })}: {catalogItems.length}
              </p>
            </div>
            <p className={css.text}>{intl.formatMessage({ id: 'Console.parts' })}: {parts}</p>
            <p className={css.text}>
              {intl.formatMessage({ id: 'Console.description' })}: {description}
            </p>
          </div>
          <div className={css.actionContainer}>
            <div className={css.actionWrapper}>
              <NamedLink 
                className={css.link} 
                name="EditProductPage" 
                params={{ id: catalogProductId, type: 'edit' }} 
                to={{ search: location.search }}
              >
                <FormattedMessage id="Console.editCatalogProduct" />
              </NamedLink>
              <span> • </span>
              <NamedLink 
                className={css.link} 
                name="EditItemPage" 
                params={{ id: '00000-0000-0000-0000', type: 'new' }} 
                to={{ search: itemSearch }}
              >
                <FormattedMessage id="Console.creatCatalogItem" />
              </NamedLink>
            </div>
            {!!catalogItems.length &&
              <button
                className={classNames(css.link, css.showBtn)}
                onClick={handleExpandClick}
              >
                {intl.formatMessage({
                  id: isExpanded ? 'Console.collapseCatalogItems' : 'Console.expandCatalogItems',
                })}
              </button>}
          </div>
        </div>
      </div>
      {isExpanded && !!catalogItems.length &&
        <>
          <h3>{intl.formatMessage({ id: "Console.catalogItemsHeading" })}</h3>
          {catalogItems
            .sort((a,b) => a.attributes.index - b.attributes.index)
            .map(item => (
            <CatalogItem 
              key={item.id.uuid} 
              catalogItem={item} 
              intl={intl} 
              productTitle={title} 
              catalogProductId={catalogProductId}
              isHighlighted={item.id.uuid === highlightedItemId}
            />
          ))}
        </>}
    </li>
  );
};

export default ProductItem;
